<script setup>
/**
 * Office Hours above the staff Sessions list — the prototype's `.now` card.
 * A drop-in, not a booked session, so it sits apart from the session rows.
 *
 *   (DR)  ● OPEN NOW · UNTIL 4:00 PM
 *         Drop-in Office Hours              [Edit] [Delete] [Open the room]
 *         Mon–Thu 2–4 PM · Fri 10 AM–12 PM · Dan Ruiz on the desk today · open to everyone
 *
 * The line is the week as its dates are scheduled, not a pattern word; the
 * face is whoever is on the desk today. Edit opens the session editor, as on
 * any session — for the owner and hub staff only; a broker gets no Edit.
 * Delete, beside it and for the same people, asks first (DeleteOfficeHoursModal).
 * Closed, it greys and says when it next opens.
 *
 * With no Office Hours at all, it says so to whoever may set one up, and
 * offers to.
 */
import { computed } from 'vue';
import XlButton from '../xl/XlButton.vue';
import IconClockCountdown from '../icons/IconClockCountdown.vue';
import IconPhone from '../icons/IconPhone.vue';
import { weekSummary } from '../../helpers/officeHours';
import { clock, weekday } from '../../helpers/sessionTimes';
import { viewerTimeZone } from '../../helpers/greeting';

const props = defineProps({
  /** From GET /office-hours/desk; null when no desk is scheduled. */
  desk: { type: Object, default: null },
  /** The owner and hub staff edit Office Hours; a broker does not. */
  canEdit: { type: Boolean, default: false },
  /** False when the hub has no Office Hours at all (not merely none ahead); null when not known. */
  exists: { type: [Boolean, null], default: null },
  timeZone: { type: String, default: undefined },
  // A test's fixed clock; otherwise the real one, read each time the card recomputes.
  now: { type: Date, default: undefined },
});

const emit = defineEmits(['open-room', 'edit', 'delete', 'set-up']);

const today = computed(() => props.desk?.today ?? null);
const isOpen = computed(() => Boolean(today.value?.open));
const youAreOn = computed(() => Boolean(today.value?.you_are_on));
const host = computed(() => String(today.value?.host?.name ?? '').trim());
const initials = computed(() => host.value.split(/\s+/).map((word) => word[0]).slice(0, 2).join('').toUpperCase());

const clockNow = () => props.now ?? new Date();
const sameDay = (iso) => new Date(iso).toDateString() === clockNow().toDateString();

const status = computed(() => {
  if (isOpen.value) return `Open now · until ${clock(today.value.ends_at, props.timeZone)}`;

  const next = props.desk?.next;
  if (!next) return 'Closed';
  const day = sameDay(next.starts_at) ? 'today' : weekday(next.starts_at, props.timeZone);

  return `Closed · opens ${day} at ${clock(next.starts_at, props.timeZone)}`;
});

/** "Mon–Thu 2–4 PM · Fri 10 AM–12 PM", on the viewer's clock. */
const week = computed(() => (props.desk?.schedule
  ? weekSummary(props.desk.schedule, { fromZone: props.desk.schedule_zone, toZone: props.timeZone ?? viewerTimeZone(), now: clockNow() })
  : ''));

const whoToday = computed(() => {
  if (!today.value?.host) return 'nobody on the desk today';
  if (youAreOn.value) return "you're on the desk today";

  return `${host.value || 'Someone'} on the desk today`;
});

const line = computed(() => [week.value, whoToday.value, 'open to everyone'].filter(Boolean).join(' · '));

const waiting = computed(() => today.value?.waiting ?? 0);
// Into the call: whoever is on the desk may open their own room before it
// opens; anyone else joins while it is open.
const canJoin = computed(() => isOpen.value || youAreOn.value);
</script>

<template>
  <section
    v-if="props.desk"
    class="desk"
    :class="{ 'desk--open': isOpen }"
    data-testid="office-hours-strip"
  >
    <span class="desk__ph" :class="{ 'desk__ph--person': isOpen && initials }" aria-hidden="true">
      <template v-if="isOpen && initials">{{ initials }}<i class="desk__ph-dot" /></template>
      <IconClockCountdown v-else color="currentColor" size="22" />
    </span>
    <div class="desk__in">
      <!-- Who is waiting sits with the desk's state, not on the button: the
           button says what it does, and keeps its length as people come and go.
           Said to everyone who sees the card — the owner too, not only whoever
           is on the desk (Office Hours → "the owner sees no waiting count"). -->
      <span class="desk__lbl" data-testid="office-hours-status"><i v-if="isOpen" class="desk__dot" />{{ status }}<template v-if="waiting"> · <b data-testid="office-hours-waiting">{{ waiting }} waiting</b></template></span>
      <h3 class="desk__h">Drop-in Office Hours</h3>
      <p class="desk__p" data-testid="office-hours-strip-text">{{ line }}</p>
    </div>
    <div class="desk__act">
      <XlButton
        v-if="props.canEdit"
        variant="ghost"
        data-testid="office-hours-edit"
        @click="emit('edit')"
      >Edit</XlButton>
      <XlButton
        v-if="props.canEdit"
        variant="ghost-danger"
        data-testid="office-hours-delete"
        @click="emit('delete')"
      >Delete</XlButton>
      <XlButton
        v-if="canJoin"
        variant="ghost"
        data-testid="office-hours-open-room"
        @click="emit('open-room', today)"
      ><IconPhone color="currentColor" size="16" />Open the room</XlButton>
    </div>
  </section>

  <section
    v-else-if="props.exists === false && props.canEdit"
    class="desk"
    data-testid="office-hours-none"
  >
    <span class="desk__ph" aria-hidden="true"><IconClockCountdown color="currentColor" size="22" /></span>
    <div class="desk__in">
      <h3 class="desk__h">No Office Hours</h3>
      <p class="desk__p">This hub has no open desk, so policyholders have no Join the call in their portal.</p>
    </div>
    <div class="desk__act">
      <XlButton data-testid="office-hours-set-up" @click="emit('set-up')">
        <IconPhone color="currentColor" size="16" />Set up Office Hours
      </XlButton>
    </div>
  </section>
</template>

<style scoped>
/* Prototype `.now` (build 7 Oct): green while open, quiet grey while closed. */
.desk {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  padding: 18px 20px;
  margin-bottom: 18px;
  border-radius: 20px;
  border: 1px solid var(--xl-color-border);
  background: linear-gradient(135deg, #F6F2F8, #FCFAFD);
  font-family: var(--xl-font-family);
}
.desk--open { background: linear-gradient(135deg, #E9F6EF, #F6FCF9); border-color: #C0E3D0; }
.desk__ph {
  position: relative;
  width: 62px; height: 62px; flex: 0 0 62px; border-radius: 50%; display: grid; place-items: center;
  background: #fff; color: var(--xl-color-secondary-text); border: 1px solid var(--xl-color-border);
}
/* Prototype `.now .ph`: today's person, on the brand gradient, with the open dot. */
.desk__ph--person {
  border: 0;
  background: linear-gradient(150deg, var(--xl-color-surface-tint), var(--xl-color-primary));
  color: #fff; font-size: 19px; font-weight: 600;
  box-shadow: 0 6px 18px rgba(94, 47, 120, 0.26);
}
.desk__ph-dot {
  position: absolute; right: 2px; bottom: 2px; width: 13px; height: 13px; border-radius: 50%;
  background: #22A05B; border: 2px solid #fff;
}
.desk--open .desk__ph:not(.desk__ph--person) { color: #166B48; border-color: #C0E3D0; }
.desk__in { flex: 1; min-width: 220px; }
.desk__lbl {
  display: inline-flex; align-items: center; gap: 6px;
  font-size: 11.5px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase;
  color: var(--xl-color-secondary-text);
}
.desk--open .desk__lbl { color: #166B48; }
.desk__dot { width: 7px; height: 7px; border-radius: 50%; background: #22A05B; }
.desk__h { display: flex; align-items: center; gap: 8px; margin: 4px 0 4px; font-size: 20px; font-weight: 600; letter-spacing: -0.02em; }
.desk__p { margin: 0; font-size: 14.5px; color: var(--xl-color-on-surface-variant); }
.desk__act { display: flex; gap: 8px; flex-wrap: wrap; }
</style>
